/**
 * Stepper — previous/play/next control trio for narrative demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useMemo, useState } from "react";
import { GRAY, PRIMARY } from "../design-system/index.js";
import { useReducedMotion } from "../hooks/index.js";

export interface StepperStep {
  readonly id: string;
  readonly label: string;
}

export interface StepperProps {
  readonly steps: readonly StepperStep[];
  readonly index?: number;
  readonly defaultIndex?: number;
  readonly onStep?: (index: number, step: StepperStep) => void;
  readonly autoplay?: boolean;
  readonly intervalMs?: number;
  readonly ariaLabel?: string;
}

function clampIndex(index: number, max: number): number {
  return Math.max(0, Math.min(max, index));
}

export function Stepper({
  steps,
  index,
  defaultIndex = 0,
  onStep,
  autoplay = false,
  intervalMs = 1400,
  ariaLabel = "Stepper controls",
}: StepperProps): React.JSX.Element {
  const reducedMotion = useReducedMotion();
  const [internalIndex, setInternalIndex] = useState(defaultIndex);
  const [playing, setPlaying] = useState(autoplay && !reducedMotion);
  const maxIndex = Math.max(0, steps.length - 1);
  const currentIndex = clampIndex(index ?? internalIndex, maxIndex);
  const currentStep = steps[currentIndex];

  const canMove = steps.length > 1;
  const setStep = useMemo(
    () =>
      (nextIndex: number): void => {
        const clamped = clampIndex(nextIndex, maxIndex);
        if (index === undefined) setInternalIndex(clamped);
        const step = steps[clamped];
        if (step !== undefined) onStep?.(clamped, step);
      },
    [index, maxIndex, onStep, steps],
  );

  useEffect(() => {
    if (!playing || reducedMotion || !canMove) return undefined;
    const handle = setInterval(() => {
      setStep(currentIndex >= maxIndex ? 0 : currentIndex + 1);
    }, intervalMs);
    return (): void => {
      clearInterval(handle);
    };
  }, [canMove, currentIndex, intervalMs, maxIndex, playing, reducedMotion, setStep]);

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      style={{
        alignItems: "center",
        display: "inline-flex",
        gap: 6,
      }}
    >
      <button
        type="button"
        aria-label="Previous step"
        disabled={!canMove || currentIndex === 0}
        onClick={() => {
          setStep(currentIndex - 1);
        }}
        style={stepperButtonStyle}
      >
        {"<"}
      </button>
      <button
        type="button"
        aria-label={playing ? "Pause" : "Play"}
        disabled={!canMove || reducedMotion}
        onClick={() => {
          setPlaying((value) => !value);
        }}
        style={{
          ...stepperButtonStyle,
          minWidth: 64,
        }}
      >
        {playing ? "Pause" : "Play"}
      </button>
      <button
        type="button"
        aria-label="Next step"
        disabled={!canMove || currentIndex === maxIndex}
        onClick={() => {
          setStep(currentIndex + 1);
        }}
        style={stepperButtonStyle}
      >
        {">"}
      </button>
      {currentStep !== undefined && (
        <span
          aria-live="polite"
          style={{
            color: GRAY[600],
            fontSize: "0.8125rem",
            marginLeft: 6,
          }}
        >
          {currentIndex + 1}/{steps.length} {currentStep.label}
        </span>
      )}
    </div>
  );
}

const stepperButtonStyle: React.CSSProperties = {
  background: "white",
  border: `1px solid ${GRAY[300]}`,
  borderRadius: 6,
  color: PRIMARY[800],
  cursor: "pointer",
  fontSize: "0.8125rem",
  fontWeight: 700,
  minHeight: 32,
  minWidth: 32,
  padding: "0.35rem 0.55rem",
};
